import { supabase } from '@/integrations/supabase/client';

// Resolves "which Google account does this feed hang off?" — the question every
// reauth prompt has to answer before it can show the right login_hint.
//
// Finding R2-14, review I5: the first version simply took the first row with
// auth_status='reauth_required', without any relation to the feed whose sync had
// failed and without an .order(). Multi-account users are a supported case
// (Settings renders a grid of accounts), so with two affected accounts the dialog
// picked a nondeterministic one and sent the user off to reconnect the wrong
// Google account.
//
// There is exactly one ownership chain in this schema and it is not optional:
//   feeds.merchant_center_id -> merchant_centers.google_account_id -> google_accounts
// There is no products.user_id and no feeds.user_id (see SCHEMA.md). RLS scopes every
// step of the chain to the signed-in user, so a foreign feed id resolves to null.

export interface FeedGoogleAccount {
  id: string;
  email: string | null;
}

/** Narrows PostgREST's embedded resource, which can arrive as an object or a 1-element array. */
function firstEmbedded<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

export async function resolveGoogleAccountForFeed(
  feedId: string,
): Promise<FeedGoogleAccount | null> {
  const { data, error } = await supabase
    .from('feeds')
    .select('merchant_centers(google_accounts(id, email))')
    .eq('id', feedId)
    .maybeSingle();

  if (error) {
    console.error('resolveGoogleAccountForFeed: ownership chain lookup failed', error);
    return null;
  }

  const merchantCenter = firstEmbedded(data?.merchant_centers);
  const account = firstEmbedded(merchantCenter?.google_accounts);
  if (!account?.id) return null;

  return { id: account.id, email: account.email ?? null };
}
